import React, { useEffect, useState, useContext } from "react";
import { Row, Col, Card, Spinner, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faTrophy, 
  faCheckCircle, 
  faFileAlt,
  faQuestionCircle 
} from "@fortawesome/free-solid-svg-icons";
import SwaggerContext from "../SwaggerContext";
import { statisticsService } from "../services/statisticsService";
import PropTypes from "prop-types";

const CATEGORY_NAMES = {
  CAUSA_EMOCION: "Causa emoción",
  EMOCION_COMPLEJA: "Emoción compleja",
  DESEO_EMOCION: "Deseo Emoción",
  PENSAMIENTO_EMOCION: "Pensamiento emoción",
  INTERACCION: "Interacciones",
};

const CATEGORY_COLORS = {
  CAUSA_EMOCION: "#3498db",
  EMOCION_COMPLEJA: "#1abc9c",
  DESEO_EMOCION: "#f1c40f",
  PENSAMIENTO_EMOCION: "#e67e22",
  INTERACCION: "#e74c3c",
};

const normalizeSessionType = (sessionType) => {
  if (!sessionType) return sessionType;
  if (sessionType.includes(",")) {
    const parts = sessionType.split(",").map(s => s.trim()).sort();
    return parts.join(",");
  }
  return sessionType;
};

const getCategoryName = (sessionType) => {
  const normalized = normalizeSessionType(sessionType);
  const allCategories = ["CAUSA_EMOCION", "EMOCION_COMPLEJA", "DESEO_EMOCION", "PENSAMIENTO_EMOCION", "INTERACCION"];
  if (normalized && normalized.includes(",")) {
    const types = normalized.split(",").map(s => s.trim()).sort();
    const isAllCategories = allCategories.every(cat => types.includes(cat)) && types.length === allCategories.length;
    if (isAllCategories) {
      return "Todas";
    }
  }
  return CATEGORY_NAMES[normalized] || sessionType;
};

const getCategoryColor = (sessionType) => {
  const normalized = normalizeSessionType(sessionType);
  const allCategories = ["CAUSA_EMOCION", "EMOCION_COMPLEJA", "DESEO_EMOCION", "PENSAMIENTO_EMOCION", "INTERACCION"];
  if (normalized && normalized.includes(",")) {
    const types = normalized.split(",").map(s => s.trim()).sort();
    const isAllCategories = allCategories.every(cat => types.includes(cat)) && types.length === allCategories.length;
    if (isAllCategories) {
      return "#c0392b";
    }
  }
  return CATEGORY_COLORS[normalized] || "#95a5a6";
};

const UserStatistics = ({ userId }) => {
  const { gameSessionApi } = useContext(SwaggerContext);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadStatistics = async () => {
      setError(null);
      setLoading(true);
      
      // Verificar si el método existe
      if (!gameSessionApi || typeof gameSessionApi.getUserStatisticsById !== 'function') {
        setError("La API no soporta obtener estadísticas de otros usuarios");
        setLoading(false);
        return;
      }
      
      const result = await statisticsService.getUserStatisticsById(gameSessionApi, userId);
      
      if (result.success) {
        setStats(result.data);
      } else {
        setError(result.error);
      }
      setLoading(false);
    };

    if (userId) {
      loadStatistics();
    }
  }, [userId, gameSessionApi]);

  if (!userId) {
    return null;
  }

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" size="sm" />
        <p className="mt-2 mb-0">Cargando estadísticas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="warning" className="mb-4">
        <p className="mb-0">No se pudieron cargar las estadísticas del usuario</p>
      </Alert>
    );
  }

  if (!stats || stats.totalSessions === 0) {
    return (
      <Alert variant="info" className="mb-4">
        <p className="mb-0">El usuario aún no tiene estadísticas</p>
      </Alert>
    );
  }

  return (
    <div className="mt-4 pt-4 border-top">
      <h5 className="mb-3">Estadísticas del Usuario</h5>
      
      <Row className="mb-3">
        <Col md={4} className="mb-3">
          <Card>
            <Card.Body className="text-center">
              <FontAwesomeIcon icon={faTrophy} size="lg" className="text-warning mb-2 d-block" />
              <h6>Sesiones Completadas</h6>
              <h4 className="mb-0">{stats.completedSessions}</h4>
              <small className="text-muted">de {stats.totalSessions} totales</small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4} className="mb-3">
          <Card>
            <Card.Body className="text-center">
              <FontAwesomeIcon icon={faCheckCircle} size="lg" className="text-success mb-2 d-block" />
              <h6>Tasa de Acierto</h6>
              <h4 className="mb-0">{stats.overallAccuracyRate.toFixed(1)}%</h4>
              <small className="text-muted">
                {stats.totalCorrectAnswers} de {stats.totalQuestionsAnswered}
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4} className="mb-3">
          <Card>
            <Card.Body className="text-center">
              <FontAwesomeIcon icon={faFileAlt} size="lg" className="text-primary mb-2 d-block" />
              <h6>Páginas Visitadas</h6>
              <h4 className="mb-0">{stats.totalPagesVisited}</h4>
              <small className="text-muted">lecciones</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {stats.categoryStatistics && stats.categoryStatistics.length > 0 && (
        <>
          <h6 className="mb-3 mt-4">Por Categoría</h6>
          <Row>
            {stats.categoryStatistics.map((category, index) => {
              const categoryName = getCategoryName(category.sessionType);
              const categoryColor = getCategoryColor(category.sessionType);
              
              return (
                <Col md={6} key={index} className="mb-3">
                  <Card>
                    <Card.Header 
                      style={{ backgroundColor: categoryColor, color: "white", padding: "12px" }}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <small className="mb-0" style={{ fontWeight: "bold" }}>{categoryName}</small>
                      <span className="badge bg-light text-dark" style={{ fontSize: "11px" }}>
                        {category.completedSessions}/{category.totalSessions}
                      </span>
                    </Card.Header>
                    <Card.Body style={{ padding: "12px" }}>
                      <Row className="text-center" style={{ fontSize: "12px" }}>
                        <Col xs={4}>
                          <FontAwesomeIcon icon={faTrophy} className="text-warning" style={{ fontSize: "14px" }} />
                          <div className="text-muted" style={{ fontSize: "10px" }}>Completadas</div>
                          <strong>{category.completedSessions}</strong>
                        </Col>
                        <Col xs={4}>
                          <FontAwesomeIcon icon={faCheckCircle} className="text-success" style={{ fontSize: "14px" }} />
                          <div className="text-muted" style={{ fontSize: "10px" }}>Acierto</div>
                          <strong>{category.accuracyRate.toFixed(1)}%</strong>
                        </Col>
                        <Col xs={4}>
                          <FontAwesomeIcon icon={faQuestionCircle} className="text-primary" style={{ fontSize: "14px" }} />
                          <div className="text-muted" style={{ fontSize: "10px" }}>Preguntas</div>
                          <strong>{category.totalQuestionsAnswered}</strong>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </>
      )}
    </div>
  );
};

UserStatistics.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default UserStatistics;
